import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { createUseStyles } from 'react-jss';

import Modal from 'react-bootstrap/Modal';
import { RootState } from '../config/store';
import Text from './Text';
import { hideAuthModals, showContinueRegisterModal, showLoginModal, showRegisterModal } from '../actions/ui';
import { Formik, Form } from 'formik';
import FormikTextField from './FormikTextField';
import FormikSelect from './FormikSelect';
import Button from './Button';
import { City, Area, Country, User, Address } from '../types';
import { getAllCities, getAllCountries, createAddress } from '../services/ApiService';
import { addUserAddress, loginLocally, setUser } from '../actions/auth';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { pageTrack, track } from '../services/mixpanelService';
import FormikErrorMessage from './FormikErrorMessage';
import {FLAVOUR} from '../config/serverInfo' 

const rePhoneNumber = /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/;


export interface AddAddressModalProps { }
const AddAddressModal: React.FC<AddAddressModalProps> = () => {
  const dispatch = useDispatch();
  const show = useSelector<RootState, boolean>(
    (state) => state.ui.showAddressModal,
  );
  const user = useSelector<RootState, User>(
    (state) => state.auth.user,
  );
  
  const canClose = useSelector<RootState, boolean>(
    (state) => state.ui.canClose,
  );
  const guest=useSelector<RootState, boolean>(
    (state) => state.ui.showGuest,
  );
  const classes = useStyles();

  const formSchema = Yup.object().shape({
    user_name:guest ? Yup.string().required() : Yup.string(),
    user_email:guest  ? Yup.string().email().required("The email is invalid") : Yup.string(),
    label: Yup.string().required(),
    phone:Yup.string().matches(rePhoneNumber,"Phone number is not valid").min(10,'At least ten numbers').required("Phone is required"),
    building: FLAVOUR == 'US' ? Yup.string() : Yup.string().required(),
    street_name: Yup.string().required(),
    zip_code:FLAVOUR == 'US' ? Yup.string().required() :Yup.string(),
  }).required();
 
  const [cities, setCities] = useState<City[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const countriesPayload = useSelector<RootState, Country[]>((state) => state.countries);
  const countriesIsDeliverySupported=useMemo(() => countriesPayload.filter((country:Country) => country.is_delivery_supported),[countriesPayload]);

  useEffect(() => {
    (async () => {
      pageTrack("add_user_address_screen");
      if(FLAVOUR=='US'){
        
        let cities = await getAllCities("&country_id=" + 17);
        setCities(cities)
        setAreas([]);
      }
    })();
  }, []);
 
  const onHide = useCallback(() => {
    if (canClose)
      dispatch(hideAuthModals());
  }, [dispatch, canClose]);
  
  const handleChangeCity = useCallback((item,setFieldValue) => {
    setFieldValue('area_id','');
    setAreas(item.areas)
  }, []
  )
 
  const handleChangeCountry = useCallback((item,setFieldValue) => {
    (async () => {
      setFieldValue('city_id','');
      setFieldValue('area_id','');
      let cities = await getAllCities("&country_id=" + item.value);
      setCities(cities)
      setAreas([]);
    })();
  }, []
  )
  const handleAddAddress = useCallback(
    (data, form) => {
      (async () => {
        try{
            const dataPayload = await createAddress({
            ...data,
            city_id: data.city_id.value,
            area_id: data.area_id.value,
            country_id:FLAVOUR=='US' ? 17 : data.country_id.value,
          });
          //register guest account
          if(dataPayload.is_guest && !dataPayload.is_exist_before){
            dispatch(setUser(dataPayload.data));
            loginLocally(dataPayload.data.token);
            form.resetForm();
            track("add_shipping_info")  
            dispatch(hideAuthModals());
          } 
          //add address to guest account
          else if(dataPayload.is_guest && dataPayload.is_exist_before  &&  dataPayload.existUserDeliverAddress){
            dispatch(addUserAddress(dataPayload.data));
            form.resetForm();
            track("add_shipping_info");  
            dispatch(hideAuthModals());
          }
          //continue register for guest account
          else if(dataPayload.is_guest && dataPayload.is_exist_before  &&  !dataPayload.existUserDeliverAddress){
            dispatch(hideAuthModals());
            form.resetForm();
            dispatch(showContinueRegisterModal(dataPayload.data));
            // dispatch(hideAuthModals());
          }
          //add address to user
          else if (!dataPayload.is_guest){
            dispatch(addUserAddress(dataPayload.data));
            form.resetForm();
            track("add_shipping_info")  
            dispatch(hideAuthModals());
          }
        }
          catch(err){
            dispatch(showLoginModal(false,true));
          }
      })();
    },
    [dispatch],
  );

  return (
    <Modal id="address-modal" onHide={onHide} centered show={show}>
      <Modal.Header closeButton={true}>
        <Modal.Title>
        <Text as="h5" className="m-0" bold>
             {guest ? 'Guest checkout' : 'Add New Address'}   
        </Text>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Formik
          onSubmit={handleAddAddress}
          validationSchema={formSchema}
          initialValues={{
            label: '',
            phone: '',
            city_id: 0,
            country_id: 0,
            area_id: 0,
            building: '',
            street_name: '',
            note: '',
            user_email:'',
            user_name:'',
            zip_code:'',
            name:user ? user?.name : '',
          }}
        >
          {({ handleSubmit, isSubmitting, isValid, values,setFieldValue}) => (
            <Form onSubmit={handleSubmit}>
              {guest ?
                (
              <>
              <Row className="py-3">
                <Col xs={12} lg={6}>
                  <FormikTextField
                    className={classes.input}
                    name="user_name"
                    placeholder="Full name *"
                  />
                </Col>
                <Col xs={12} lg={6}>
                <FormikTextField
                  className={classes.input}
                  name="name"
                  placeholder="Name *"
                />
                </Col>
              </Row>
              <div className="py-2">
              <FormikErrorMessage name="user_email" />
                  <FormikTextField
                    className={classes.input}
                    name="user_email"
                    type="email"
                    placeholder="Email *"
                  />
              </div>
              </>):(null)}
              {
                FLAVOUR == 'US' ? (
                  <div className="py-2">
                  <FormikTextField
                    className={classes.input}
                    name="label"
                    placeholder="Label* (eg. Home,Work)"
                  />
                  </div>
                ) : (
                  <Row className="py-3">
                    <Col xs={12} lg={6}>
                      <FormikTextField
                        className={classes.input}
                        name="label"
                        placeholder="Label* (eg. Home,Work)"
                      />
                    </Col>
                    <Col xs={12} lg={6}>
                      <FormikTextField
                        className={classes.input}
                        name="street_name"
                        placeholder="Area,Street *"
                      />
                    </Col>
                </Row>
                )
              }

              {FLAVOUR == "US" ? (null) : (
                <div className="py-2">
                <FormikTextField
                  className={classes.input}
                  name="building"
                  placeholder="Building,Floor *"
                />
                </div>
              )}
              
              {FLAVOUR=="US" ? (
              <Row className="py-2">
                <Col xs={12} lg={6}>
                  <FormikTextField
                    className={classes.input}
                    name="zip_code"
                    type="text"
                    placeholder="Zip code *"
                  />
                </Col>
                <Col xs={12} lg={6}>
              <FormikErrorMessage name="phone" />

                <FormikTextField
                    className={classes.input}
                    name="phone"
                    
                    placeholder="Phone *"
                />
                </Col>
              </Row> 
              ) : (null)}

              {FLAVOUR != 'US' ? (
                <div className="py-2">
                <FormikSelect
                  required
                  name="country_id"
                  change={(item:any)=>{handleChangeCountry(item,setFieldValue)}}
                  options={countriesIsDeliverySupported.map((country) => ({
                    label: country.long_name,
                    value: country.id,
                  }))}
                  
                  placeholder="Country"
                  styles={{
                    control: (styles: any) => ({
                      ...styles,
                      backgroundColor: '#fff',
                      padding: '6px',
                      border: '2px solid #dadada',
                      borderRadius: 5,
                      width: '100%',
                    }),
                  }}
                />
              </div>
              ):(null) }
              
              <div className="py-2">
                    <FormikSelect
                      name="city_id"
                      change={(item:any)=>{handleChangeCity(item,setFieldValue)}}
                      options={cities.map((city) => ({
                        label: city.name,
                        value: city.id,
                        areas: city.areas
                      }))}
    
                      placeholder={FLAVOUR == 'US' ?  "State":"City"}
                      styles={{
                        control: (styles: any) => ({
                          ...styles,
                          backgroundColor: '#fff',
                          padding: '6px',
                          border: '2px solid #dadada',
                          borderRadius: 5,
                          width: '100%',
                        }),
                      }}
                    />
                  </div>
              

              <div className="py-2">
                <FormikSelect
                  name="area_id"
                  options={areas.map((area) => ({
                    label: area.name,
                    value: area.id,
                  }))}
                  placeholder={FLAVOUR == 'US' ?  "City":"Area"}
                  styles={{
                    control: (styles: any) => ({
                      ...styles,
                      backgroundColor: '#fff',
                      padding: '6px',
                      border: '2px solid #dadada',
                      borderRadius: 5,
                      width: '100%',
                    }),
                  }}
                />
              </div>

              {FLAVOUR =='US' ? (
                <>
                <div className="py-2">
                <FormikTextField
                  className={classes.input}
                  name="street_name"
                  placeholder="Address line 1 *"
                />
                </div>
                <div className="py-2">
                <FormikTextField
                  className={classes.input}
                  name="building"
                  placeholder="Address line 2"
                />
                </div>
                </>) : (null)}

                {FLAVOUR !='US' ? (
                <Row className="py-2">
                    <Col xs={12} lg={6}>
                    <FormikTextField
                      className={classes.input}
                      name="note"
                      placeholder="Note,Landmarks"
                    />
                    </Col>
                    <Col xs={12} lg={6}>
                    <FormikErrorMessage name="phone" />
                    <FormikTextField
                      className={classes.input}
                      name="phone"
                      placeholder="Phone *"
                    />
                    </Col>
                </Row>
                ) : (null) }
              <div className="py-2">
                <Button
                  type="submit"
                  color="primary"
                  disabled={!values.area_id || !values.city_id || !isValid || isSubmitting}
                  block
                >
                  <Text as="span" color="white">
                    Add
                  </Text>
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

const useStyles = createUseStyles({
  modal: {
    width: '100%',
    maxWidth: '500px',
  },
  input: {
    backgroundColor: '#fff',
  },
  '@media (max-width:990px)':{
    input: {
      marginBottom:'10px',
    },
  },
});
export default AddAddressModal;
